// Small shared pieces: chips, stat tiles, sparkline, markdown rendering,
// long-text collapsing.

import { useState, type CSSProperties } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {
  Confidence,
  GateNumber,
  ParsedSection,
  RunSummary,
  VerdictNorm,
} from "@ideacrunch/shared";
import { useVocab } from "../vocab";

export function VerdictChip({
  norm,
  raw,
}: {
  norm: VerdictNorm | null;
  raw: string;
}) {
  const { verdictGloss } = useVocab();
  const cls =
    norm === "SUPPORTED" || norm === "APPROVE"
      ? "good"
      : norm === "WEAK" || norm === "REVISE"
        ? "warn"
        : norm === "REFUTED" || norm === "REJECT"
          ? "critical"
          : norm === "UNVERIFIABLE"
            ? "serious"
            : "ghost";
  return <span className={`chip ${cls}`}>{verdictGloss(norm, raw)}</span>;
}

export function ConfidenceBadge({
  confidence,
  modelKnowledge,
}: {
  confidence: Confidence | null;
  modelKnowledge: boolean;
}) {
  if (modelKnowledge) {
    return <span className="chip serious">model knowledge - capped low</span>;
  }
  if (!confidence) return null;
  const cls =
    confidence === "high" ? "good" : confidence === "medium" ? "ghost" : "warn";
  return <span className={`chip ${cls}`}>confidence: {confidence}</span>;
}

export function StaleBadge({ asOf }: { asOf: string | null }) {
  if (!asOf) return null;
  const m = /^(\d{4})-?(\d{2})?/.exec(asOf);
  if (!m) return <span className="chip ghost">as-of {asOf}</span>;
  const age =
    Date.now() - new Date(Number(m[1]), Number(m[2] ?? "1") - 1).getTime();
  const stale = age > 365 * 24 * 3600 * 1000;
  return (
    <span className={`chip ${stale ? "warn" : "ghost"}`}>
      as-of {asOf}
      {stale ? " (stale)" : ""}
    </span>
  );
}

export function StatTile({
  value,
  label,
  tone,
}: {
  value: string | number;
  label: string;
  tone?: "good" | "warn" | "critical";
}) {
  return (
    <div className={`stat-tile ${tone ?? ""}`}>
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

/**
 * Mini pipeline: phase segments with gate diamonds, same state colors as the
 * full graph so the visual language is learned once.
 */
export function Sparkline({ run }: { run: RunSummary }) {
  const segs: { key: string; gate?: GateNumber; phase?: number }[] = [
    { key: "intake", phase: 0 },
    { key: "g0", gate: 0 },
    { key: "plan", phase: 1 },
    { key: "research", phase: 2 },
    { key: "skeptic", phase: 3 },
    { key: "merge", phase: 4 },
    { key: "g1", gate: 1 },
    { key: "blueprint", phase: 5 },
    { key: "skeptic2", phase: 6 },
    { key: "g2", gate: 2 },
    { key: "archive", phase: 7 },
  ];
  return (
    <div className="sparkline">
      {segs.map((s) => {
        let cls = "seg";
        if (s.gate !== undefined) cls += " gate";
        if (s.gate !== undefined && run.pendingGate === s.gate) {
          cls += " needs-you";
        } else if (s.gate !== undefined) {
          const gatePhase = [0, 4, 6][s.gate]!;
          if (run.phase > gatePhase) cls += " done";
        } else if (s.phase !== undefined && run.phase >= s.phase) {
          cls += " done";
        }
        return <span key={s.key} className={cls} />;
      })}
    </div>
  );
}

// One shared definition of "long" so collapsing behaves the same on every
// surface. Simple mode hides verbosity sooner than technical mode.
function useLongTextLimits() {
  const { simple } = useVocab();
  return simple
    ? { mdChars: 450, mdLines: 8, plainChars: 180 }
    : { mdChars: 900, mdLines: 14, plainChars: 260 };
}

export function MarkdownView({
  text,
  collapsible = false,
}: {
  text: string;
  collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { mdChars, mdLines } = useLongTextLimits();
  const long =
    collapsible &&
    (text.length > mdChars || text.split("\n").length > mdLines);
  const clamped = long && !expanded;
  const body = (
    <div className={clamped ? "md clamp-box" : "md"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: (props) => (
            <div className="md-table-wrap">
              <table {...props} />
            </div>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
      {clamped && <div className="clamp-fade" />}
    </div>
  );
  if (!long) return body;
  const words = text.split(/\s+/).filter(Boolean).length;
  return (
    <div>
      {body}
      <button
        className="chip ghost clickable clamp-toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "Show less" : `Show all (${words} words)`}
      </button>
    </div>
  );
}

/**
 * Plain-string counterpart to the collapsible MarkdownView: long strings get
 * cut at a word boundary with an inline expand affordance.
 */
export function LongText({
  text,
  max,
  style,
}: {
  text: string;
  max?: number;
  style?: CSSProperties;
}) {
  const [expanded, setExpanded] = useState(false);
  const { plainChars } = useLongTextLimits();
  const limit = max ?? plainChars;
  const t = text.trim();
  if (t.length <= limit + 60) {
    return <span style={style}>{t}</span>;
  }
  const cut = t.slice(0, limit).replace(/\s+\S*$/, "");
  return (
    <span style={style}>
      {expanded ? t : `${cut}...`}{" "}
      <button className="linkish" onClick={() => setExpanded((v) => !v)}>
        {expanded ? "less" : "more"}
      </button>
    </span>
  );
}

/** Slice a section's body (heading excluded) out of the raw markdown. */
export function sectionText(raw: string, section: ParsedSection): string {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  return lines.slice(section.lineStart, section.lineEnd).join("\n").trim();
}

export function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function statusLabel(run: RunSummary): string {
  switch (run.status) {
    case "awaiting-gate-0":
      return "Your call: confirm the framing";
    case "awaiting-gate-1":
      return "Your call: pick the direction";
    case "awaiting-gate-2":
      return "Your call: approve the plan";
    case "complete":
      return "Complete";
    case "killed":
      return "Killed";
    default:
      return "In progress";
  }
}

/**
 * Download link for the state-of-affairs briefing: a point-in-time markdown
 * snapshot compiled for outside feedback (a trusted person, another model)
 * before the pending decision. Renders only while a gate is pending - the
 * server refuses the export at any other time - and the document is never
 * stored in the run, so the machine cannot read it back as evidence.
 */
export function BriefingDownload({ run }: { run: RunSummary }) {
  if (run.pendingGate === null) return null;
  return (
    <div className="panel" style={{ marginTop: 12 }}>
      <h2>Want outside eyes before you decide?</h2>
      <p style={{ color: "var(--ink-2)", fontSize: 13.5, margin: "0 0 10px" }}>
        Download a one-file snapshot of everything so far - framing,
        deal-breakers, evidence, and the open decision - to share with a
        person or another AI. It lives outside the run: nothing here will
        ever read it, and feedback comes back only through your decision.
      </p>
      <a
        className="btn secondary"
        href={`/api/runs/${encodeURIComponent(run.id)}/briefing`}
        download
      >
        Download the briefing (.md)
      </a>
    </div>
  );
}
